import { esc } from '../lib/format.js';
import { t as vt, locale } from '../lib/i18n.js';

// ── STANDAARDTABEL ────────────────────────────────────────────────────────
// Eén tabelcomponent voor alle overzichten buiten de teamtabel, met dezelfde
// bediening: sorteren door op een kop te klikken, filteren per kolom, en één
// zoekveld over alle kolommen tegelijk.
//
// De teamtabel blijft voorlopig zijn eigen implementatie houden. Die heeft
// selectie, bulkacties, kolomzichtbaarheid en paginering die hier niet in
// zitten; hem nu omzetten zou het belangrijkste scherm van de applicatie
// raken zonder dat er iets aan functionaliteit bij komt.
//
// Een kolom is: { veld, label, breedte, uitlijn, mono, filter, cel, tekst }
//   tekst(rij) levert de waarde waarop gezocht, gefilterd en gesorteerd wordt
//   cel(rij)   levert de weergave; standaard de ontsnapte tekst
const tabellen = {};

export function tabelTeken(sleutel, doelId, kolommen, rijen, opties = {}) {
  const vorige = tabellen[sleutel]?.staat;
  tabellen[sleutel] = {
    doelId, kolommen, rijen, opties,
    staat: vorige || { zoek: '', veld: opties.sorteerVeld || null, richting: opties.sorteerRichting || 1, filters: {} },
  };
  tabelHerteken(sleutel);
}

function tabelTekst(kol, rij) {
  if (kol.tekst) return String(kol.tekst(rij) ?? '');
  return String(rij[kol.veld] ?? '');
}

function tabelRijen(sleutel) {
  const t = tabellen[sleutel];
  const { zoek, veld, richting, filters } = t.staat;
  let rijen = t.rijen.slice();

  Object.entries(filters).forEach(([f, filter]) => {
    const kol = t.kolommen.find(k => k.veld === f);
    if (!kol) return;
    rijen = rijen.filter(r => {
      const waarde = tabelTekst(kol, r).trim();
      if (filter.type === 'empty') return waarde === '' || waarde === '—';
      if (filter.type === 'notempty') return waarde !== '' && waarde !== '—';
      return waarde.toLowerCase().includes(String(filter.value).toLowerCase());
    });
  });

  if (zoek.trim()) {
    const naald = zoek.trim().toLowerCase();
    rijen = rijen.filter(r => t.kolommen.some(k => tabelTekst(k, r).toLowerCase().includes(naald)));
  }

  if (veld) {
    const kol = t.kolommen.find(k => k.veld === veld);
    if (kol) {
      rijen.sort((a, b) => {
        const x = tabelTekst(kol, a), y = tabelTekst(kol, b);
        const nx = parseFloat(x.replace(',', '.')), ny = parseFloat(y.replace(',', '.'));
        const getallen = !Number.isNaN(nx) && !Number.isNaN(ny)
          && /^[-\d.,\s€%]+$/.test(x) && /^[-\d.,\s€%]+$/.test(y);
        const uitkomst = getallen ? nx - ny : x.localeCompare(y, locale(), { sensitivity: 'base' });
        return uitkomst * richting;
      });
    }
  }
  return rijen;
}

export function tabelHerteken(sleutel) {
  const t = tabellen[sleutel];
  if (!t) return;
  const el = document.getElementById(t.doelId);
  if (!el) return;

  const zichtbaar = tabelRijen(sleutel);
  const aantalFilters = Object.keys(t.staat.filters).length;
  const s = esc(sleutel);

  const koppen = t.kolommen.map(k => {
    const pijl = t.staat.veld === k.veld
      ? `<i class="ti ti-${t.staat.richting === 1 ? 'arrow-up' : 'arrow-down'} tabel-pijl"></i>` : '';
    const filterKnop = k.filter === false ? '' :
      `<button class="col-filter-btn" onclick="App.tabelFilter('${s}','${esc(k.veld)}',this)" title="${esc(vt('tabel.filterkolom'))}"><i class="ti ti-filter"></i></button>`;
    const tag = t.staat.filters[k.veld]
      ? `<div class="col-filter-tag">${t.staat.filters[k.veld].type === 'contains'
           ? esc(t.staat.filters[k.veld].value) : vt(t.staat.filters[k.veld].type === 'empty' ? 'tabel.tagleeg' : 'tabel.taggevuld')}</div>` : '';
    return `<th data-col="${esc(k.veld)}"${k.breedte ? ` style="width:${k.breedte}"` : ''}>
      <div class="col-header">
        <span class="tabel-sorteer" onclick="App.tabelSorteer('${s}','${esc(k.veld)}')">${esc(k.label)}${pijl}</span>
        ${filterKnop}
      </div>${tag}</th>`;
  }).join('');

  // Klikbare rijen: opties.rijKlik(rij). De index verwijst naar de zichtbare
  // volgorde, die bij elke hertekening opnieuw wordt vastgelegd.
  t.zichtbaar = zichtbaar;
  const klik = t.opties.rijKlik;
  const lichamen = zichtbaar.map((r, i) => `<tr${klik ? ` class="tabel-klikbaar" onclick="App.tabelRijKlik('${s}',${i})"` : ''}>${t.kolommen.map(k => {
    const stijl = [k.uitlijn ? `text-align:${k.uitlijn}` : '', k.mono ? 'font-family:var(--mono)' : '']
      .filter(Boolean).join(';');
    return `<td${stijl ? ` style="${stijl}"` : ''}>${k.cel ? k.cel(r) : esc(tabelTekst(k, r))}</td>`;
  }).join('')}</tr>`).join('');

  el.innerHTML = `
    <div class="tabel-balk">
      <div class="tabel-zoek">
        <i class="ti ti-search"></i>
        <input type="text" id="tabelZoek_${s}" placeholder="${esc(t.opties.zoekLabel || vt('tabel.zoeken'))}"
               value="${esc(t.staat.zoek)}" oninput="App.tabelZoek('${s}',this.value)">
      </div>
      <span class="tabel-telling">${vt('tabel.telling', { zichtbaar: zichtbaar.length, totaal: t.rijen.length })}</span>
      ${(t.staat.zoek || aantalFilters)
        ? `<button class="btn btn-ghost btn-sm" onclick="App.tabelWisAlles('${s}')"><i class="ti ti-filter-off"></i> ${vt('tabel.alleswissen')}</button>` : ''}
    </div>
    <div class="table-wrap">
      <table class="tabel-standaard">
        <thead><tr>${koppen}</tr></thead>
        <tbody>${lichamen}</tbody>
      </table>
      ${zichtbaar.length ? '' : `<div class="empty-state"><i class="ti ti-search-off"></i><strong>${vt('tabel.nietsgevonden')}</strong><p>${t.rijen.length ? vt('tabel.pasaan') : esc(t.opties.leeg || vt('tabel.leeg'))}</p></div>`}
    </div>`;

  // De cursor zou anders naar het begin springen bij elke toetsaanslag.
  const zoekveld = document.getElementById(`tabelZoek_${sleutel}`);
  if (zoekveld && t.staat.zoekActief) {
    zoekveld.focus();
    zoekveld.setSelectionRange(zoekveld.value.length, zoekveld.value.length);
  }
}

export function tabelZoek(sleutel, waarde) {
  const t = tabellen[sleutel];
  if (!t) return;
  t.staat.zoek = waarde;
  t.staat.zoekActief = true;
  tabelHerteken(sleutel);
}

export function tabelSorteer(sleutel, veld) {
  const t = tabellen[sleutel];
  if (!t) return;
  t.staat.zoekActief = false;
  if (t.staat.veld === veld) t.staat.richting = t.staat.richting === 1 ? -1 : 1;
  else { t.staat.veld = veld; t.staat.richting = 1; }
  tabelHerteken(sleutel);
}

export function tabelWisAlles(sleutel) {
  const t = tabellen[sleutel];
  if (!t) return;
  t.staat.zoek = '';
  t.staat.filters = {};
  t.staat.zoekActief = false;
  tabelHerteken(sleutel);
}

export function tabelFilter(sleutel, veld, knop) {
  document.querySelectorAll('.col-filter-pop').forEach(p => p.remove());
  const t = tabellen[sleutel];
  if (!t) return;
  const kol = t.kolommen.find(k => k.veld === veld);
  const huidig = t.staat.filters[veld] || {};
  const rect = knop.getBoundingClientRect();

  const pop = document.createElement('div');
  pop.className = 'col-filter-pop';
  pop.style.cssText = `top:${rect.bottom + 6}px;left:${Math.min(rect.left, window.innerWidth - 256)}px`;
  pop.innerHTML = `
    <div class="col-filter-pop-title">${esc(vt('tabel.filter', { kolom: kol?.label || veld }))}</div>
    <div class="col-filter-presets">
      <button class="col-filter-preset ${huidig.type === 'empty' ? 'active' : ''}"
              onclick="App.tabelFilterToepassen('${esc(sleutel)}','${esc(veld)}','empty','')">${vt('tabel.filterleeg')}</button>
      <button class="col-filter-preset ${huidig.type === 'notempty' ? 'active' : ''}"
              onclick="App.tabelFilterToepassen('${esc(sleutel)}','${esc(veld)}','notempty','')">${vt('tabel.filtergevuld')}</button>
    </div>
    <input id="tabelFilterInput" type="text" placeholder="${esc(vt('tabel.bevat'))}"
           value="${huidig.type === 'contains' ? esc(huidig.value || '') : ''}"
           onkeydown="if(event.key==='Enter')App.tabelFilterToepassen('${esc(sleutel)}','${esc(veld)}','contains',this.value)">
    <div class="col-filter-pop-footer">
      <button class="btn btn-ghost btn-sm" onclick="App.tabelFilterWissen('${esc(sleutel)}','${esc(veld)}')"><i class="ti ti-x"></i> ${vt('tabel.wissen')}</button>
      <button class="btn btn-primary btn-sm" onclick="App.tabelFilterToepassen('${esc(sleutel)}','${esc(veld)}','contains',document.getElementById('tabelFilterInput').value)"><i class="ti ti-check"></i> ${vt('tabel.toepassen')}</button>
    </div>`;
  document.getElementById('dropdownContainer').appendChild(pop);
  setTimeout(() => document.getElementById('tabelFilterInput')?.focus(), 30);
  setTimeout(() => {
    document.addEventListener('click', function sluit(e) {
      if (!pop.contains(e.target) && !knop.contains(e.target)) {
        pop.remove();
        document.removeEventListener('click', sluit);
      }
    });
  }, 10);
}

export function tabelFilterToepassen(sleutel, veld, type, waarde) {
  document.querySelectorAll('.col-filter-pop').forEach(p => p.remove());
  const t = tabellen[sleutel];
  if (!t) return;
  t.staat.zoekActief = false;
  if (type === 'empty' || type === 'notempty') t.staat.filters[veld] = { type };
  else if (waarde && waarde.trim()) t.staat.filters[veld] = { type: 'contains', value: waarde.trim() };
  else delete t.staat.filters[veld];
  tabelHerteken(sleutel);
}

export function tabelFilterWissen(sleutel, veld) {
  document.querySelectorAll('.col-filter-pop').forEach(p => p.remove());
  const t = tabellen[sleutel];
  if (!t) return;
  t.staat.zoekActief = false;
  delete t.staat.filters[veld];
  tabelHerteken(sleutel);
}

export function tabelRijKlik(sleutel, index) {
  const t = tabellen[sleutel];
  const rij = t?.zichtbaar?.[index];
  if (rij && t.opties.rijKlik) t.opties.rijKlik(rij);
}

// Opnieuw tekenen, bijvoorbeeld na een taalwissel.
export function tabelVernieuw(sleutel) { tabelHerteken(sleutel); }
